import { useCallback, useEffect, useReducer, useRef } from "react";
import {
  ChatAttachmentTableInfo,
  deleteChatAttachment,
  uploadChatAttachment,
} from "@shared/api/chatApi";
import { ApiError } from "@shared/type/ApiType";

/**
 * 업로드 표 파일 상태 머신(첨부 draft §38·§40·§43·§44) — 붙여넣기 텍스트 칩과는 별개 상태.
 * File 참조는 업로드 요청 동안만 보유하고, READY에는 정제된 표시명·불투명 참조·테이블 메타만 남긴다.
 * localStorage/sessionStorage/IndexedDB에 아무것도 쓰지 않는다.
 */

export type ReadyAttachment = {
  attachmentId: string;
  /** 로컬 표시 전용(서버 미전송) — 제어·bidi 제거·공백 축약·120자 캡. */
  displayName: string;
  tables: ChatAttachmentTableInfo[];
  selectedTableId?: string;
};

export type ChatAttachmentState =
  | { status: "IDLE" }
  | { status: "UPLOADING"; displayName: string; prev?: ReadyAttachment }
  | { status: "READY"; ready: ReadyAttachment }
  | { status: "REMOVING"; ready: ReadyAttachment };

export type ChatAttachmentAction =
  | { type: "UPLOAD_START"; displayName: string; prev?: ReadyAttachment }
  | { type: "UPLOAD_SUCCESS"; ready: ReadyAttachment }
  | { type: "UPLOAD_FAILURE" }
  | { type: "REMOVE_START" }
  | { type: "REMOVE_SUCCESS" }
  | { type: "REMOVE_FAILURE" }
  | { type: "SELECT_TABLE"; tableId: string }
  | { type: "CLEAR_LOCAL" };

export function attachmentReducer(state: ChatAttachmentState, action: ChatAttachmentAction): ChatAttachmentState {
  switch (action.type) {
    case "UPLOAD_START":
      return { status: "UPLOADING", displayName: action.displayName, prev: action.prev };
    case "UPLOAD_SUCCESS":
      return { status: "READY", ready: action.ready };
    case "UPLOAD_FAILURE":
      // 교체 실패는 직전 READY 복원(§40) — 최초 업로드 실패는 IDLE
      return state.status === "UPLOADING" && state.prev
        ? { status: "READY", ready: state.prev }
        : { status: "IDLE" };
    case "REMOVE_START":
      return state.status === "READY" ? { status: "REMOVING", ready: state.ready } : state;
    case "REMOVE_SUCCESS":
    case "CLEAR_LOCAL":
      return { status: "IDLE" };
    case "REMOVE_FAILURE":
      // 네트워크/5xx — 서버 제거 미확인이므로 READY 복원(§58)
      return state.status === "REMOVING" ? { status: "READY", ready: state.ready } : state;
    case "SELECT_TABLE":
      return state.status === "READY"
        ? { status: "READY", ready: { ...state.ready, selectedTableId: action.tableId } }
        : state;
    default:
      return state;
  }
}

/** 표시명 정제(§22) — 제어·bidi 문자 공백화, 공백 축약, 120자 캡. React 텍스트로만 렌더. */
export function sanitizeDisplayName(name: string): string {
  let cleaned = "";
  for (const ch of name) {
    const code = ch.codePointAt(0) ?? 0;
    const control =
      code < 0x20 ||
      (code >= 0x7f && code <= 0x9f) ||
      code === 0x061c ||
      code === 0x200e ||
      code === 0x200f ||
      (code >= 0x202a && code <= 0x202e) ||
      (code >= 0x2066 && code <= 0x2069);
    cleaned += control ? " " : ch;
  }
  const collapsed = cleaned.replace(/\s+/g, " ").trim();
  const chars = Array.from(collapsed);
  return chars.length <= 120 ? collapsed : chars.slice(0, 120).join("") + "…";
}

/** 확장자 종별 판정 — 미지원은 null(정직 거절은 호출측 안내). */
export function attachmentKindOf(fileName: string): "xlsx" | "csv" | null {
  const lower = fileName.toLowerCase();
  if (lower.endsWith(".xlsx")) return "xlsx";
  if (lower.endsWith(".csv")) return "csv";
  return null;
}

export function useChatAttachment() {
  const [state, dispatch] = useReducer(attachmentReducer, { status: "IDLE" });
  /** 로컬 세대(§58) — 교체/제거/클리어마다 증가. 늦게 도착한 구 응답 페이로드를 걸러낸다. */
  const generationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(
    () => () => {
      // 언마운트 — 브라우저 요청만 best-effort 중단(서버 취소 주장 금지, §38)
      abortRef.current?.abort();
    },
    []
  );

  const selectFile = useCallback(
    async (file: File): Promise<{ ok: boolean; message?: string }> => {
      const current = stateRef.current;
      if (current.status === "UPLOADING" || current.status === "REMOVING") {
        return { ok: false, message: "파일 처리 중입니다. 잠시 후 다시 시도해 주세요." };
      }
      const kind = attachmentKindOf(file.name);
      if (!kind) {
        return { ok: false, message: "xlsx 또는 csv 파일만 업로드할 수 있습니다." };
      }
      const displayName = sanitizeDisplayName(file.name) || `upload.${kind}`;
      const prev = current.status === "READY" ? current.ready : undefined;
      const myGeneration = ++generationRef.current;
      const abort = new AbortController();
      abortRef.current = abort;
      dispatch({ type: "UPLOAD_START", displayName, prev });
      try {
        const response = await uploadChatAttachment(file, kind, abort.signal);
        if (generationRef.current !== myGeneration) {
          return { ok: false }; // 뒤늦은 구 응답 — UI 상태 갱신 금지(§40)
        }
        const data = response.data;
        if (!data?.attachmentId || !Array.isArray(data.tables) || data.tables.length === 0) {
          dispatch({ type: "UPLOAD_FAILURE" });
          return { ok: false, message: "업로드 응답이 올바르지 않습니다." };
        }
        dispatch({
          type: "UPLOAD_SUCCESS",
          ready: {
            attachmentId: data.attachmentId,
            displayName,
            tables: data.tables,
            selectedTableId: data.selectedTableId ?? undefined,
          },
        });
        return { ok: true };
      } catch (error) {
        if (generationRef.current !== myGeneration) {
          return { ok: false };
        }
        dispatch({ type: "UPLOAD_FAILURE" });
        const message =
          error instanceof ApiError
            ? error.message
            : "파일 업로드 중 오류가 발생했습니다.";
        return { ok: false, message };
      } finally {
        // File/AbortController 참조 해제(§43) — READY에는 정제 메타만 남는다
        if (abortRef.current === abort) {
          abortRef.current = null;
        }
      }
    },
    []
  );

  const remove = useCallback(async (): Promise<{ ok: boolean; message?: string }> => {
    const current = stateRef.current;
    if (current.status !== "READY") {
      return { ok: false };
    }
    generationRef.current += 1;
    dispatch({ type: "REMOVE_START" });
    try {
      await deleteChatAttachment(current.ready.attachmentId);
      dispatch({ type: "REMOVE_SUCCESS" });
      return { ok: true };
    } catch (error) {
      if (error instanceof ApiError && error.code === 410) {
        dispatch({ type: "REMOVE_SUCCESS" }); // 이미 만료/제거 — 410은 권위 있는 정리 신호(§58)
        return { ok: true };
      }
      dispatch({ type: "REMOVE_FAILURE" });
      return {
        ok: false,
        message: "서버에서 첨부 제거를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.",
      };
    }
  }, []);

  /** 로컬 상태만 소거(§58) — 410 수신·채팅 클리어용. 서버 정리 주장은 하지 않는다. */
  const clearLocal = useCallback(() => {
    generationRef.current += 1;
    abortRef.current?.abort();
    dispatch({ type: "CLEAR_LOCAL" });
  }, []);

  const selectTable = useCallback((tableId: string) => {
    dispatch({ type: "SELECT_TABLE", tableId });
  }, []);

  return {
    state,
    /** 현재 로컬 세대 — 전송 시 캡처해 응답 도착 시 비교(§58). */
    generation: () => generationRef.current,
    selectFile,
    remove,
    clearLocal,
    selectTable,
  };
}

export type ChatAttachmentHook = ReturnType<typeof useChatAttachment>;
